import { observable, action } from 'mobx';
import { useEffect } from 'react';
import useMount from '@src/utils/hooks/use-mount';
import { px2rem2 } from '../../../src/utils/rem-util';

class WikiModuleStore {
  @observable tooltipVisible = false;
  @observable tooltipContent = {};
  // 当前激活的项key（格式：type_key，如：tag_1、force_0、character_2）
  @observable activeKey = '';
  // 新增：标记鼠标是否在浮窗内
  @observable isTooltipHovered = false;
  // 新增：存储延迟隐藏的定时器ID，用于清理
  hideTooltipTimer = null;

  // 关联人物
  @observable characterDataMap = null;
  // 关联势力
  @observable factionDataMap = null;

  // 浮窗内容映射
  @observable tooltipDataMap = null;

  // 直接存储滚动容器DOM
  @observable scrollContainer = null;
  // 浮窗定位
  @observable tooltipPosition = {};

  constructor(options = {}) {
    Object.assign(this, options);
  }

  //设置滚动容器ref
  setScrollContainer = container => {
    this.scrollContainer = container;
  };

  // 统一处理鼠标移入目标元素（词条/势力/人物） 需要优化数据还没拿到
  @action
  handleTargetEnter = (type, key, data, targetId, element) => {
    const uniqueIndex = element.currentTarget.getAttribute('uniqueIndex');

    // 1. 快速切换时，立即清理之前的延迟隐藏定时器
    if (this.hideTooltipTimer) {
      clearTimeout(this.hideTooltipTimer);
      this.hideTooltipTimer = null;
    }
    // 计算调试浮窗位置
    this.openTooltipPosition(targetId, element.currentTarget);

    // 2. 生成新的激活key，优先响应新的移入事件
    const newActiveKey = type === 'tag' ? `${type}_${key}_${uniqueIndex}` : `${type}_${key}`;
    if (!data && (!this.tooltipDataMap || !this.tooltipDataMap[key])) {
      // this.tooltipVisible = false;
      this.activeKey = '';
      this.tooltipContent = {};
      return;
    }
    // 4. 设置浮窗内容和状态
    this.tooltipContent = type === 'tag' ? this.tooltipDataMap[key] || {} : data || {};
    this.activeKey = newActiveKey;
    this.tooltipVisible = true;
    // 5. 移入新元素时，重置浮窗hover状态（避免旧状态干扰）
    this.isTooltipHovered = false;
  };

  /**
   * 计算Tooltip的定位样式（适配动态渲染词条场景）
   * @param {string} targetId - 触发词条的父容器ID
   * @param {HTMLElement} triggerElem - 被hover/点击的词条DOM元素（建议传e.currentTarget）
   * @returns {Object} Tooltip定位样式 { left, top }
   */
  openTooltipPosition = (targetId, triggerElem) => {
    // ===== 1. 配置化定位参数（方便维护，不用在逻辑里找硬编码值）=====
    const POS_CONFIG = {
      // desc: { leftNear: '60%', leftFar: `-${px2rem2(490)}rem`, top: '98%' },
      // other: { topNear: '98%', topFar: '98%', left: '96%' },
      desc: { leftNear: '90%', leftFar: `-${px2rem2(460)}rem`, top: '98%' },
      other: { topNear: '98%', topFar: '98%', left: '96%' },
      debug: false // 上线前改为false，关闭调试日志
    };

    // ===== 2. 强化健壮性校验（覆盖React场景常见问题）=====
    // 校验核心参数
    if (!targetId || !triggerElem) {
      POS_CONFIG.debug && console.warn('[Tooltip定位] 缺少核心参数：targetId/triggerElem', { targetId, triggerElem });
      return {};
    }
    // 校验DOM有效性（兼容React ref、元素已卸载、方法不存在）
    const validTriggerElem = triggerElem.current || triggerElem; // 兼容React ref
    if (
      typeof validTriggerElem.getBoundingClientRect !== 'function' ||
      validTriggerElem.offsetParent === null // 元素已卸载/不可见
    ) {
      POS_CONFIG.debug && console.warn('[Tooltip定位] triggerElem不是有效DOM', validTriggerElem);
      return {};
    }

    // 获取核心DOM（兼容React ref）
    const targetBox = document.getElementById(targetId);
    const scrollContainer = this.scrollContainer.current || this.scrollContainer; // 兼容React ref
    if (!targetBox || !scrollContainer) {
      POS_CONFIG.debug && console.warn('[Tooltip定位] 未找到容器DOM', { targetId, scrollContainerExist: !!scrollContainer });
      return {};
    }

    // ===== 3. 获取位置/尺寸（缓存结果，避免重复调用）=====
    const targetBoxRect = targetBox.getBoundingClientRect();
    const triggerElemRect = validTriggerElem.getBoundingClientRect();
    const scrollContainerRect = scrollContainer.getBoundingClientRect();

    // 校验尺寸有效性（避免除以0、尺寸为0导致的判断错误）
    if (targetBoxRect.width === 0 || scrollContainerRect.height === 0) {
      POS_CONFIG.debug && console.warn('[Tooltip定位] 容器尺寸异常', { targetBoxRect, scrollContainerRect });
      return {};
    }

    // ===== 4. 核心定位逻辑（拆分逻辑，提升可读性）=====
    const position = {};
    if (targetId === 'desc') {
      // desc场景：触发元素相对于targetBox的水平位置
      const triggerRelativeLeft = triggerElemRect.left - targetBoxRect.left;
      const isNearLeft = triggerRelativeLeft < targetBoxRect.width / 2.5;
      position.left = isNearLeft ? POS_CONFIG.desc.leftNear : POS_CONFIG.desc.leftFar;
      position.top = POS_CONFIG.desc.top;

      // 调试日志（按需开启）
      POS_CONFIG.debug &&
        console.log('[Tooltip定位-desc]', {
          finalPosition: position
        });
    } else {
      const triggerRelativeLeft = triggerElemRect.left - targetBoxRect.left;
      const isNearLeft = triggerRelativeLeft < targetBoxRect.width / 2.5;
      const targetRelativeTop = targetBoxRect.top - scrollContainerRect.top;
      const isNearBottom = targetRelativeTop > scrollContainerRect.height / 2;
      position.top = isNearBottom ? POS_CONFIG.other.topFar : POS_CONFIG.other.topNear;
      // position.left = POS_CONFIG.other.left;
      position.left = isNearLeft ? POS_CONFIG.desc.leftNear : POS_CONFIG.desc.leftFar;

      // 调试日志（按需开启）
      POS_CONFIG.debug &&
        console.log('[Tooltip定位-other]', {
          finalPosition: position
        });
    }
    // console.log('position', position);
    this.tooltipPosition = position;
    // return (this.tooltipPosition = position);
  };

  // 新增：鼠标移入浮窗
  @action
  handleTooltipEnter = () => {
    // 移入浮窗时，清理延迟隐藏定时器
    if (this.hideTooltipTimer) {
      clearTimeout(this.hideTooltipTimer);
      this.hideTooltipTimer = null;
    }
    this.isTooltipHovered = true;
  };

  // 新增：鼠标移出浮窗
  @action
  handleTooltipLeave = () => {
    this.isTooltipHovered = false;
    // 移出浮窗后立即检查是否隐藏（无延迟，提升响应速度）
    this.checkHideTooltip();
  };

  // 鼠标移出目标元素
  @action
  handleTargetLeave = () => {
    // 1. 清理旧的定时器，避免重复触发
    if (this.hideTooltipTimer) {
      clearTimeout(this.hideTooltipTimer);
    }
    // 2. 缩短延迟时间（80ms），提升快速切换时的响应速度
    this.hideTooltipTimer = setTimeout(() => {
      this.checkHideTooltip();
      this.hideTooltipTimer = null; // 执行后清空定时器ID
    }, 80);
  };

  // 新增：检查是否需要隐藏浮窗（目标元素和浮窗都离开时）
  @action
  checkHideTooltip = () => {
    // 双重判断：浮窗未被hover + 无激活的目标元素
    if (!this.isTooltipHovered && this.activeKey) {
      this.tooltipVisible = false;
      this.activeKey = '';
      this.tooltipContent = {}; // 清空内容，释放内存
    }
    this.isTooltipHovered = false; // 重置hover状态
  };

  // 新增：手动清理定时器（避免内存泄漏）
  @action
  clearHideTimer = () => {
    if (this.hideTooltipTimer) {
      clearTimeout(this.hideTooltipTimer);
      this.hideTooltipTimer = null;
    }
  };

  use() {
    useMount(async () => { });
    useEffect(() => {
      return () => {
        // 组件卸载时，清理定时器和重置所有状态
        this.clearHideTimer();
        this.tooltipVisible = false;
        this.activeKey = '';
        this.isTooltipHovered = false;
        this.tooltipContent = {};
        this.characterDataMap = null;
        this.factionDataMap = null;
      };
    }, []);
  }
}

export default WikiModuleStore;
